import { ComponentRef } from '@angular/core';
import { IncidentVisualizerComponent } from '../../components/shared/incident-visualizer/incident-visualizer.component';
import { DefaultDeleteCommand, DefaultEditCommand, DefaultFlyToCommand, DefaultGoToTimeCommand, DefaultShowHideCommand, DefaultTrackingCommand, FmvCommand } from '../models/command';
import { DefaultSelectionIndicatorCommand } from '../models/default-selection-indicator-command';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { WindowService } from './window.service';
import { DataContextService } from './data-context.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from './timeline.service';
import { DefaultEditDialogComponent } from '../../components/shared/default-edit-dialog/default-edit-dialog.component';


export interface CenterPoint {
  latitude?: number;
  longitude?: number;
  altitude?: number;
}

export interface MetaData {
  [key: string]: any;
}

export interface Attribute {
  getVisualizer(): any ;
}

export interface Command {
  type: any;
  // Define command interface based on your command structure
}

export class BaseMapEntity {
  public name: string;
  public layerPath: string;
  public id: string;
  public meta_data: MetaData = {};
  public temporal_metadata: MetaData = {};
  public commands: any[] = [];
  public attachments: any[] = [];
  public cesiumEntity: any = undefined;
  public centerpoint: CenterPoint = {};
  public icon: string;
  public timestamp: any = undefined;
  public source: any = undefined;
  public attributes: Attribute[] = [];
  public isVisibleInLayersUI: boolean = true;
  public isVisibleInOverlappingEntitiesUI: boolean = true;
  public windowId: string;
  public addedToMap: boolean = false;
  public isTargeted: boolean = false;
  public goOnClick: boolean = true;
  public showIn3d: boolean = true;
  public showIn2d: boolean = false;
  public defaultSelectionIndicatorCommand: DefaultSelectionIndicatorCommand;
  public dataSourceKey?: string;

  private static readonly DEFAULT_MAP_ENTITY_ICON = 'assets/img/default_entity.png';

  private incidentVisualizerRef: ComponentRef<IncidentVisualizerComponent> | null = null;
  constructor(
    mapEntityName: string,
    mapEntityLayerPath: string,
    public cameraService: CameraService,
    public coreService: CoreService,
    public windowService: WindowService,
    public dataContext: DataContextService,
    public modalService: NgbModal,
    public timelineService: TimelineService
  ) {
    this.name = mapEntityName;
    this.layerPath = mapEntityLayerPath;
    this.id = this.generateGuid();
    this.icon = BaseMapEntity.DEFAULT_MAP_ENTITY_ICON;
    this.windowId = 'window-' + this.id;

    this.addBaseCommands(this.commands);
    this.defaultSelectionIndicatorCommand = new DefaultSelectionIndicatorCommand(this.coreService);
    this.commands.push(this.defaultSelectionIndicatorCommand);
  }


  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getId(): string {
    return this.id;
  }

  getName(): string {
    return this.name;
  }

  setName(name: string): void {
    this.name = name;
  }

  getLayerPath(): string {
    return this.layerPath;
  }

  setLayerPath(layerPath: string): void {
    this.layerPath = layerPath;
  }

  getTimestamp(): any {
    return this.timestamp;
  }

  setTimestamp(timestamp: any): void {
    this.timestamp = timestamp;
  }

  getIcon(): string {
    return this.icon;
  }

  setIcon(icon?: string): void {
    if (icon !== undefined) {
      this.icon = icon;
    } else {
      this.icon = BaseMapEntity.DEFAULT_MAP_ENTITY_ICON;
    }
  }

  getSource(): any {
    return this.source;
  }

  setSource(source: any): void {
    this.source = source;
  }

  setCenterLatitude(latitude: number): void {
    this.centerpoint.latitude = latitude;
  }

  setCenterLongitude(longitude: number): void {
    this.centerpoint.longitude = longitude;
  }

  setCenterAltitude(altitude: number): void {
    this.centerpoint.altitude = altitude;
  }

  getCenterLatitude(): number | undefined {
    return this.centerpoint.latitude;
  }

  getCenterLongitude(): number | undefined {
    return this.centerpoint.longitude;
  }

  getCenterAltitude(): number | undefined {
    return this.centerpoint.altitude;
  }

  buildEntity(): void {}

  getCesiumEntity(): any {
    this.cesiumEntity.arcLiveMapEntity = this;
    return this.cesiumEntity;
  }

  viewDetailsWindow(
    scope: any,
    positioning?: string,
    autoSize?: boolean,
    closeCallBack?: () => void,
    windowShape?: string,
    doNotDrag?: boolean,
    showDefaultView?: boolean
  ): void {
    if(this.hasCustomCamera && this.hasCustomCamera() && (scope?.sessionOptionsService?.sessionData?.mode=== 'Live Mode' || scope?.sessionOptionsService?.sessionData?.mode === 'Playback Mode')) {
      this.cameraService.useEntityCamera(this);
    } else if (this.hasCustomCamera && this.hasCustomCamera()) {
      // For Monitor Mode: if scope is an entity (has id), use it; otherwise use this entity
      // This maintains backward compatibility while fixing the notification flow
      const entityToUse = (scope && typeof scope === 'object' && scope.id) ? scope : this;
      this.cameraService.useEntityCamera(entityToUse);
    } else {
      const windowId = 'window-' + this.id;
      if (!this.windowService.windowIsOpen(windowId)) {
        this.windowService.openWindows[windowId] = '';
        this.cameraService.openDetailsWindow(this, windowId);
      }
    }
    
  }

  getDetailsWindow(positioning?: string, windowShape?: string, doNotDrag?: boolean): string {
    // Return HTML string or component reference as needed
    return `<div>Details for ${this.name}</div>`;
  }

  getDetailsWindowHeaderId(): string {
    return 'header-' + this.getWindowId();
  }

  getDetailsWindowHeader(): string {
    return `<details-window-header name='' id='${this.getDetailsWindowHeaderId()}' entityid='${this.id}'></details-window-header>`;
  }

  getDetailsWindowContents(): string {
    let concatenated = '';
    const arrayLength = this.getAttributes().length;
    for (let i = 0; i < arrayLength; i++) {
      concatenated = concatenated.concat(this.getAttributes()[i].getVisualizer());
    }
    return concatenated;
  }

  getDetailsWindowFooterId(): string {
    return 'footer-' + this.getWindowId();
  }

  getDetailsWindowFooter(): string {
    return `<details-window-footer name='' id='${this.getDetailsWindowFooterId()}' entityid='${this.id}'></details-window-footer>`;
  }

  showEditWindow(mapEntity: any) {
    if (this.windowService) {
      return this.windowService.openModalWindow(
          this,
          DefaultEditDialogComponent,
          "",  undefined,
          undefined,
          undefined,
          { mapEntity: this },
        );
    }
    return Promise.resolve();
  }

  async goToEntity(goToEntityCallback?: () => void): Promise<void> {
    if (typeof this.cameraService?.flyToEntity === 'function') {
      const promise = this.cameraService.flyToEntity(this.cesiumEntity);
      if (promise) {
        await promise;
      }
    }
    if (goToEntityCallback) {
      goToEntityCallback();
    }
  }

  flyToPositionWithOptions(
    lat: number, 
    lon: number, 
    heading: number, 
    pitch: number, 
    roll: number, 
    height: number, 
    duration?: number
  ): void {
    // this.coreService.getViewer().camera.flyTo({
    //   destination: Cesium.Cartesian3.fromDegrees(lon, lat, height),
    //   duration: duration || 3,
    //   orientation: {
    //     heading: Cesium.Math.toRadians(heading),
    //     pitch: Cesium.Math.toRadians(pitch),
    //     roll: roll
    //   }
    // });
  }

  openEntityWindownOnSelection(): boolean {
    // Implement your logic or return a default value
    return true;
  }

  goToEntityOnSelection(): boolean {
    return true;
  }

  hide(): void {
    if (this.cesiumEntity !== undefined) {
      this.cesiumEntity.show = false;
    }
  }

  show(): void {
    if (this.cesiumEntity !== undefined) {
      this.cesiumEntity.show = true;
    }
  }

  isVisible(): boolean {
    if (this.cesiumEntity !== undefined) {
      return this.cesiumEntity.show;
    }
    return false;
  }

  getCommands(): Command[] {
    return this.commands;
  }

  addCommand(command: Command): void {
    this.commands.push(command);
  }

  removeCommand(command: Command): void {
    const index = this.commands.indexOf(command);
    if (index > -1) {
      this.commands.splice(index, 1);
    }
  }

  removeDeleteCommand(): void {
    for (let i = 0; i < this.commands.length; i++) {
      if (this.commands[i] instanceof DefaultDeleteCommand) {
        this.removeCommand(this.commands[i]);
        break;
      }
    }
  }

  removeShowHideCommand(): void {
    for (let i = 0; i < this.commands.length; i++) {
      if (this.commands[i] instanceof DefaultShowHideCommand) {
        this.removeCommand(this.commands[i]);
        break;
      }
    }
  }

  removeTimelineCommand(): void {
    for (let i = 0; i < this.commands.length; i++) {
      if (this.commands[i] instanceof DefaultGoToTimeCommand) {
        this.removeCommand(this.commands[i]);
        break;
      }
    }
  }

  removeTrackCommand(): void {
    for (let i = 0; i < this.commands.length; i++) {
      if (this.commands[i] instanceof DefaultTrackingCommand) {
        this.removeCommand(this.commands[i]);
        break;
      }
    }
  }

  addBaseCommands(commands: any[]) {
    commands.push(new DefaultDeleteCommand(this.modalService, this.dataContext,this.windowService));
    commands.push(new DefaultShowHideCommand());
    commands.push(new DefaultFlyToCommand());
    commands.push(new DefaultEditCommand());
    commands.push(new DefaultGoToTimeCommand(this.timelineService));
    commands.push(new FmvCommand(this.windowService));
    commands.push(new DefaultTrackingCommand(this.coreService));
  }

  getAttributes(): Attribute[] {
    return this.attributes;
  }

  addAttribute(attribute: Attribute): void {
    this.attributes.push(attribute);
  }

  removeAttribute(attribute: Attribute): void {
    const index = this.attributes.indexOf(attribute);
    if (index > -1) {
      this.attributes.splice(index, 1);
    }
  }

  onDelete(): void {
    // Override in subclasses
  }

  isPrimitive(): boolean {
    return false;
  }

  showInLayersUI(): boolean {
    return this.isVisibleInLayersUI;
  }

  setShowInLayersUI(show: boolean): void {
    this.isVisibleInLayersUI = show;
  }

  showInOverlappingEntitiesUI(): boolean {
    return this.isVisibleInOverlappingEntitiesUI;
  }

  setShowInOverlappingEntitiesUI(show: boolean): void {
    this.isVisibleInOverlappingEntitiesUI = show;
  }

  getWindowId(): string {
    return this.windowId;
  }

  setWindowId(windowId: string): void {
    this.windowId = windowId;
  }

  getIsAddedMap(): boolean {
    return this.addedToMap;
  }

  setAddedToMap(status: boolean): void {
    this.addedToMap = status;
  }

  setIsTargeted(isTargeted: boolean): void {
    this.isTargeted = isTargeted;
    // this.defaultSelectionIndicatorCommand.setSelectionIcon(isTargeted);
  }

  addMetaData(key: string, value: any): void {
    this.meta_data[key] = value;
  }

  deleteMetaData(key: string): void {
    delete this.meta_data[key];
  }

  getMetaData(): MetaData {
    return this.meta_data;
  }

  addTemporalMetaData(key: string, value: any): void {
    this.temporal_metadata[key] = value;
  }

  deleteTemporalMetaData(key: string): void {
    delete this.temporal_metadata[key];
  }

  getTemporalMetaData(): MetaData {
    return this.temporal_metadata;
  }

  isArchiveable(): boolean {
    return false;
  }

  archive(): void {
    // Override in subclasses
  }

  unarchive(): void {
    // Override in subclasses
  }

  hasCustomCamera(): boolean {
    return false;
  }

  getCustomCamera(): any {
    return null;
  }

  getCustomCameraSettings(): { showCloseButton: boolean } {
    return { showCloseButton: true };
  }

  setDoNotGoOnClick(value: boolean): void {
    this.goOnClick = value;
  }

  getShowIn2d(): boolean {
    return this.showIn2d;
  }

  setShowIn2d(value: boolean): void {
    this.showIn2d = value;
  }

  getShowIn3d(): boolean {
    return this.showIn3d;
  }

  setShowIn3d(value: boolean): void {
    this.showIn3d = value;
  }

  get2dIcon(): any {
    return undefined;
  }

  setDataSourceKey(key: string): void {
    this.dataSourceKey = key;
  }

  getDataSourceKey(): string | undefined {
    return this.dataSourceKey;
  }
}